import * as THREE from 'three'
import { Skier, emptyInput } from './physics/skier'
import type { InputFrame, Pose } from './physics/skier'
import { Sluff } from './physics/sluff'
import { TUNE } from './physics/tuning'
import { Controls } from './input/controls'
import { World } from './render/world'
import { TerrainMesh } from './render/terrainMesh'
import type { ColorMode } from './render/terrainMesh'
import { ObstacleMesh } from './render/obstacleMesh'
import { SkierMesh } from './render/skierMesh'
import type { RiderState } from './render/skierMesh'
import { ChaseCamera } from './render/chaseCamera'
import { Spray } from './render/spray'
import { Spindrift } from './render/spindrift'
import { SluffMesh } from './render/sluffMesh'
import { Backdrop } from './render/backdrop'
import { TrackRibbon } from './render/tracks'
import { Hud } from './debug/hud'
import { Race } from './race/race'
import { GhostPlayer, GhostRecorder, loadBest, saveBest } from './race/ghost'
import { Autopilot } from './race/autopilot'
import type { GhostRecording } from './race/ghost'
import { SLOPES, DEFAULT_SLOPE_ID } from './slopes/roster'
import { rebuildSlope } from './worker/rebuild'
import { buildPayload } from './worker/buildPayload'
import type { BuildDone, BuildProgress, BuildRequest, SlopePayload } from './worker/generate.worker'
import { angleDelta, clamp01, lerp } from './core/math'

/**
 * FALL LINE — time trial.
 *
 * A race down a designed slope, against the clock, the par line and your own
 * best run. The fixed-timestep loop is unchanged and stays that way: 60Hz
 * simulation decoupled from render, input latched and quantised once per tick,
 * render interpolated between the last two ticks (spec §7). Ghosts, replays and
 * a defensible leaderboard all rest on that.
 */

const DT = 1 / TUNE.TICK_HZ

function setLoader(title: string, sub: string): void {
  const mark = document.querySelector('.loader-mark')
  const line = document.querySelector('.loader-sub')
  if (mark) mark.textContent = title
  if (line) line.textContent = sub
}

/**
 * Register the offline cache.
 *
 * Skipped for `file:` — service workers are not allowed there, and the
 * single-file build is already an offline app by virtue of being one file.
 */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || location.protocol === 'file:') return
  const register = (): void => {
    navigator.serviceWorker.register(new URL('./sw.js', document.baseURI))
      .catch((err) => console.warn('[fall line] offline cache unavailable', err))
  }
  // `boot` is called from a double requestAnimationFrame, which by then is
  // usually *after* `load` has fired — so waiting for `load` unconditionally
  // attached a listener to an event that had already been and gone, and the
  // worker never registered. Nothing visible broke; the game simply had no
  // offline cache, and the install prompt never appeared.
  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register, { once: true })
}

function boot(): void {
  registerServiceWorker()
  const canvas = document.getElementById('game') as HTMLCanvasElement
  const params = new URLSearchParams(location.search)
  const slopeId = params.get('slope') ?? localStorage.getItem('fall-line/slope') ?? DEFAULT_SLOPE_ID

  setLoader('FALL LINE', 'shaping the slope…')

  // The single-file build injects the worker's compiled source as a string so
  // the whole game can ship as one self-contained page; normally we load it as
  // a module URL. Either way, some hosts refuse to start workers at all, so
  // there is a main-thread fallback below — a frozen load screen for a few
  // seconds beats no game.
  //
  // Note this deliberately does *not* use Vite's `?worker&inline`: that
  // transform emitted the worker's source unquoted into the main bundle, which
  // broke the production build with a top-level syntax error while the dev
  // server stayed perfectly healthy.
  let worker: Worker | null = null
  try {
    const injected = (window as unknown as { __FL_WORKER_SRC__?: string }).__FL_WORKER_SRC__
    worker = injected
      ? new Worker(URL.createObjectURL(new Blob([injected], { type: 'text/javascript' })))
      : new Worker(new URL('./worker/generate.worker.ts', import.meta.url), { type: 'module' })
  } catch {
    worker = null
  }

  if (!worker) {
    buildOnMainThread(canvas, slopeId)
    return
  }

  let settled = false
  const fallback = (): void => {
    if (settled) return
    settled = true
    worker?.terminate()
    console.warn('[fall line] worker unavailable, building on the main thread')
    buildOnMainThread(canvas, slopeId)
  }

  worker.onerror = fallback
  worker.onmessageerror = fallback

  // A worker blocked by CSP does not reliably fire onerror — it can simply
  // never respond. The first progress message arrives within milliseconds, so
  // silence past a few seconds means it is never coming.
  let heard = false
  const silenceTimer = setTimeout(() => { if (!heard) fallback() }, 4000)

  worker.onmessage = (e: MessageEvent<BuildProgress | BuildDone>) => {
    heard = true
    clearTimeout(silenceTimer)
    if (e.data.type === 'progress') {
      setLoader('FALL LINE', `${e.data.stage}…`)
      return
    }
    if (settled) return
    settled = true
    worker?.terminate()
    start(canvas, e.data.payload)
  }

  worker.postMessage({ slopeId } satisfies BuildRequest)
}

/**
 * Synchronous fallback. Yields to the browser between stages so the loading
 * screen at least repaints its label before each freeze.
 */
function buildOnMainThread(canvas: HTMLCanvasElement, slopeId: string): void {
  setLoader('FALL LINE', 'shaping the slope…')
  setTimeout(() => {
    const payload = buildPayload(slopeId, (stage) => setLoader('FALL LINE', `${stage}…`))
    setTimeout(() => start(canvas, payload), 0)
  }, 50)
}

function start(canvas: HTMLCanvasElement, payload: SlopePayload): void {
  const { hf, obstacles } = rebuildSlope(payload)
  localStorage.setItem('fall-line/slope', payload.slopeId)

  // --- Sim -----------------------------------------------------------------
  const skier = new Skier(hf, obstacles)
  const sluff = new Sluff(hf)
  const race = new Race(payload.checkpointZ, payload.finishZ, payload.start.z)
  const autopilot = new URLSearchParams(location.search).get('autopilot') != null
  const pilot = new Autopilot()

  const recorder = new GhostRecorder()
  skier.reset(payload.start.x, payload.start.z, 0)

  // ?ghosts=off hides the ghost riders — the screenshot harness uses it, since
  // the autopilot rides the par line and the par ghost otherwise sits exactly
  // on top of the rider being photographed.
  const hideGhosts = new URLSearchParams(location.search).get('ghosts') === 'off'
  let best: GhostRecording | null = loadBest(payload.slopeId, payload.slopeVersion)
  let bestPlayer: GhostPlayer | null = best ? new GhostPlayer(hf, obstacles, best) : null

  // --- Render --------------------------------------------------------------
  const world = new World(canvas)
  world.resize()
  const terrain = new TerrainMesh(hf, world.quality.detailFadeStart, world.quality.detailFadeEnd, obstacles)
  world.scene.add(terrain.group)
  const obstacleMesh = new ObstacleMesh(obstacles, hf)
  world.scene.add(obstacleMesh.group)
  const skierMesh = new SkierMesh(hf)
  skierMesh.addTo(world.scene)
  const bestMesh = new SkierMesh(hf, 0xe8c85f)
  bestMesh.addTo(world.scene)
  if (hideGhosts) bestMesh.setVisible(false)
  const spray = new Spray()
  world.scene.add(spray.points)
  const spindrift = new Spindrift(hf)
  world.scene.add(spindrift.points)
  const sluffMesh = new SluffMesh(sluff)
  world.scene.add(sluffMesh.points)

  // The range beyond the slope, so the terrain sits in a world rather than
  // ending at a fog wall.
  const elevation = hf.elevationRange()
  const backdrop = new Backdrop(
    new THREE.Vector3(0, 0, payload.length * 0.4),
    elevation.min,
    elevation.max,
    // Distance haze is blue-grey, not the near-white of the horizon sky.
    // Fading the range toward the sky's own colour makes it disappear into it.
    new THREE.Color(0xa9c4dc),
  )
  world.scene.add(backdrop.group)

  // Tracks. The ghosts lay theirs too, which is how a player who is keeping up
  // gets to see the line the par run took.
  const myTrack = new TrackRibbon(hf, 0x93accc, 0.42)
  const parTrack = new TrackRibbon(hf, 0x5fa8e8, 0.3)
  const bestTrack = new TrackRibbon(hf, 0xe8c85f, 0.3)
  world.scene.add(myTrack.mesh, parTrack.mesh, bestTrack.mesh)

  const chase = new ChaseCamera(hf, world.aspect)
  const controls = new Controls(canvas)
  const hud = new Hud(document.body)

  console.info(`[fall line] ${payload.name} · ${Math.round(payload.vertical)}m vert · ` +
    `sinks ${payload.sinks} · built ${payload.buildMs.toFixed(0)}ms`)

  // --- Loop state ----------------------------------------------------------
  let acc = 0
  let last = performance.now()
  let paused = false
  let colorMode: ColorMode = 'snow'
  let lastInput: InputFrame = emptyInput()
  let wasAirborne = false
  let fps = 60
  let savedThisRun = false
  let lastResult: { time: number; improved: boolean } | null = null

  const renderPose: Pose = { x: 0, y: 0, z: 0, heading: 0, roll: 0, pitch: 0 }
  const ghostPose: Pose = { x: 0, y: 0, z: 0, heading: 0, roll: 0, pitch: 0 }
  const groundNormal = { x: 0, y: 1, z: 0 }
  const flatNormal = { x: 0, y: 1, z: 0 }
  // Reused every frame — the rider rig reads these instead of a long arg list.
  const riderState: RiderState = {
    airborne: false, crashing: false, tucking: false, braking: false,
    sketchy: 0, driftYaw: 0, landSeverity: 0,
    popCooldown: 0, load: 0, hardness: 0, speed: 0,
  }
  const ghostState: RiderState = { ...riderState }
  const fwd = new THREE.Vector3()

  function restart(): void {
    skier.reset(payload.start.x, payload.start.z, 0)
    sluff.reset()
    race.reset()
    recorder.reset()
    bestPlayer?.reset()
    acc = 0
    savedThisRun = false
    lastResult = null
    spray.clear()
    sluffMesh.clear()
    myTrack.clear()
    parTrack.clear()
    bestTrack.clear()
    chase.snap()
    wasAirborne = false
  }

  window.addEventListener('keydown', (e) => {
    if (e.repeat) return
    switch (e.code) {
      case 'KeyR': restart(); break
      case 'KeyP': paused = !paused; break
      case 'KeyH': hud.setVisible(!hud.isVisible()); break
      case 'KeyB': {
        const on = backdrop.toggleHighlight()
        console.info(`[fall line] backdrop highlight ${on ? 'ON — the range is magenta' : 'off'}`)
        break
      }
      case 'KeyG':
        colorMode = colorMode === 'snow' ? 'slope' : colorMode === 'slope' ? 'hazard' : 'snow'
        terrain.setColorMode(colorMode)
        break
      case 'BracketRight': case 'BracketLeft': {
        // Cycle slopes. A proper picker is the next thing to build.
        const i = SLOPES.findIndex((s) => s.id === payload.slopeId)
        const d = e.code === 'BracketRight' ? 1 : SLOPES.length - 1
        const next = SLOPES[(i + d) % SLOPES.length]
        localStorage.setItem('fall-line/slope', next.id)
        location.search = `?slope=${next.id}`
        break
      }
    }
  })

  window.addEventListener('resize', () => {
    world.resize()
    chase.setAspect(world.aspect)
  })

  document.getElementById('loader')?.remove()

  function frame(now: number): void {
    requestAnimationFrame(frame)
    const frameDt = Math.min((now - last) / 1000, 0.25)
    last = now
    fps = lerp(fps, 1 / Math.max(frameDt, 1e-4), 0.08)

    controls.update(frameDt)

    let ticks = 0
    if (!paused) {
      acc += frameDt
      while (acc >= DT && ticks < TUNE.MAX_TICKS_PER_FRAME) {
        skier.savePrevPose()
        lastInput = controls.sample()

        // `?autopilot` skis the slope with the reactive bot instead of the
        // player. Added for the headless screenshot harness. It used to ride
        // the par recording, which was elegant — same gate, same inputs, clock
        // lands on par — but playback drifted one collision out of sync and
        // then photographed a crashed rider for the rest of the run, and par
        // itself is gone now. The bot is reactive, so it cannot drift: it
        // steers off what is actually in front of it this tick.
        if (autopilot) pilot.step(skier, hf, obstacles, lastInput)

        const wasReady = race.state === 'ready'
        race.step(skier, lastInput, DT)
        // Only record once the clock is running, so the recording and the race
        // clock share an origin and the ghost lines up tick for tick.
        if (race.state === 'running') {
          recorder.record(lastInput)
        }

        skier.sluffLoad = sluff.step(skier, DT)
        skier.step(lastInput, DT)

        // Ghosts move only while the race is live.
        if (!wasReady && race.state === 'running') {
          bestPlayer?.step(DT)
        }

        acc -= DT
        ticks++
      }
      if (acc > DT * TUNE.MAX_TICKS_PER_FRAME) acc = 0
    }

    if (race.finished && !savedThisRun) {
      savedThisRun = true
      const rec = recorder.finish(
        payload.slopeId, payload.slopeVersion, payload.start,
        race.time, race.splits.map((s) => s.time), Date.now(),
      )
      const improved = !best || rec.totalTime < best.totalTime
      if (improved) {
        best = rec
        saveBest(rec)
        bestPlayer = new GhostPlayer(hf, obstacles, rec)
      }
      lastResult = { time: race.time, improved }
    }

    const alpha = clamp01(acc / DT)
    const p = skier.prev
    const c = skier.curr
    renderPose.x = lerp(p.x, c.x, alpha)
    renderPose.y = lerp(p.y, c.y, alpha)
    renderPose.z = lerp(p.z, c.z, alpha)
    renderPose.heading = p.heading + angleDelta(p.heading, c.heading) * alpha
    renderPose.roll = lerp(p.roll, c.roll, alpha)
    renderPose.pitch = lerp(p.pitch, c.pitch, alpha)

    groundNormal.x = skier.nx
    groundNormal.y = skier.ny
    groundNormal.z = skier.nz

    if (!paused) {
      fwd.set(Math.sin(renderPose.heading), 0, Math.cos(renderPose.heading))
      const grounded = !skier.airborne && skier.crashTimer <= 0
      if (grounded && skier.speed > 2.5) {
        const speedT = clamp01(skier.speed / 20)
        const workT = clamp01(skier.gripUsed * 0.8 + skier.skid * 0.16)
        spray.emit(
          renderPose.x - fwd.x * 0.9, skier.groundY - skier.snow.sink * 0.5, renderPose.z - fwd.z * 0.9,
          skier.vx, skier.vy, skier.vz,
          skier.snow.spray * speedT * (0.3 + workT),
          clamp01(speedT * (0.45 + workT * 0.9)), frameDt,
        )
      }
      if (wasAirborne && !skier.airborne) {
        spray.emit(renderPose.x, skier.groundY, renderPose.z, skier.vx, skier.vy, skier.vz,
          (30 + skier.lastLandSeverity * 90) / Math.max(frameDt, 1e-3),
          clamp01(0.5 + skier.lastLandSeverity), frameDt)
      }
      if (skier.crashTimer > 0 && skier.speed > 3) {
        spray.emit(renderPose.x, skier.groundY, renderPose.z,
          skier.vx, skier.vy, skier.vz, 140, 0.85, frameDt)
      }
      spray.update(frameDt)
    spindrift.update(frameDt)
      sluffMesh.update()

      // Skis only cut a groove when they are actually on the snow.
      myTrack.update(renderPose.x, renderPose.z, renderPose.heading, grounded)
      trackGhost(bestPlayer, bestTrack)
    }
    wasAirborne = skier.airborne

    chase.update(renderPose, skier.speed, skier.edge, skier.airborne, frameDt)
    riderState.airborne = skier.airborne
    riderState.crashing = skier.crashTimer > 0
    riderState.tucking = lastInput.tuck
    riderState.braking = lastInput.brake
    riderState.sketchy = clamp01(skier.sketchTimer / TUNE.SKETCH_TIME)
    riderState.driftYaw = skier.speed > 1
      ? angleDelta(renderPose.heading, Math.atan2(skier.vx, skier.vz))
      : 0
    riderState.landSeverity = skier.lastLandSeverity
    riderState.popCooldown = skier.popCooldown
    riderState.load = skier.gripUsed
    riderState.hardness = clamp01(1 - skier.snow.grip / 1.6)
    riderState.speed = skier.speed
    skierMesh.update(renderPose, groundNormal, riderState, frameDt)
    if (!hideGhosts) {
      updateGhost(bestPlayer, bestMesh, alpha, frameDt)
    }
    // Which forest buckets get the detailed build. Cheap — a distance test
    // per bucket, roughly ten of them — and it is what keeps the branched
    // conifers affordable.
    obstacleMesh.update(chase.camera.position)
    world.adaptQuality(frameDt)
    terrain.update(chase.camera.position)
    world.followSun(renderPose.x, renderPose.y, renderPose.z)
    world.render(chase.camera)

    hud.update(skier, race, {
      fps, ticks,
      slopeName: payload.name,
      slopeBlurb: payload.blurb,
      difficulty: payload.difficulty,
      conditions: payload.conditions,
      vertical: payload.vertical,
      bestTime: best?.totalTime ?? null,
      bestZ: bestPlayer && !bestPlayer.finished ? bestPlayer.skier.z : null,
      lastResult,
      sluffLoad: skier.sluffLoad,
      colorMode,
      quality: `${world.quality.tier} · ${world.renderer.getPixelRatio().toFixed(1)}x`,
      drawCalls: world.renderer.info.render.calls,
      triangles: world.renderer.info.render.triangles,
    }, frameDt)
  }

  function trackGhost(g: GhostPlayer | null, ribbon: TrackRibbon): void {
    if (!g || g.finished) return
    const s = g.skier
    ribbon.update(s.x, s.z, s.heading, !s.airborne && s.crashTimer <= 0)
  }

  function updateGhost(g: GhostPlayer | null, mesh: SkierMesh, alpha: number, dt: number): void {
    if (!g) { mesh.setVisible(false); return }
    mesh.setVisible(!g.finished)
    if (g.finished) return
    const gp = g.prevPose
    const gc = g.pose
    ghostPose.x = lerp(gp.x, gc.x, alpha)
    ghostPose.y = lerp(gp.y, gc.y, alpha)
    ghostPose.z = lerp(gp.z, gc.z, alpha)
    ghostPose.heading = gp.heading + angleDelta(gp.heading, gc.heading) * alpha
    ghostPose.roll = lerp(gp.roll, gc.roll, alpha)
    ghostPose.pitch = lerp(gp.pitch, gc.pitch, alpha)
    flatNormal.x = g.skier.nx
    flatNormal.y = g.skier.ny
    flatNormal.z = g.skier.nz
    ghostState.airborne = g.skier.airborne
    ghostState.crashing = g.skier.crashTimer > 0
    ghostState.tucking = false
    ghostState.braking = false
    ghostState.sketchy = clamp01(g.skier.sketchTimer / TUNE.SKETCH_TIME)
    ghostState.driftYaw = g.skier.speed > 1
      ? angleDelta(ghostPose.heading, Math.atan2(g.skier.vx, g.skier.vz))
      : 0
    ghostState.landSeverity = g.skier.lastLandSeverity
    ghostState.popCooldown = g.skier.popCooldown
    ghostState.load = g.skier.gripUsed
    ghostState.hardness = clamp01(1 - g.skier.snow.grip / 1.6)
    ghostState.speed = g.skier.speed
    mesh.update(ghostPose, flatNormal, ghostState, dt)
  }

  requestAnimationFrame(frame)

  // Exposed in production too. This is a build people are testing, and every
  // rendering bug so far has been one I could only pin down by driving the
  // *shipped* bundle — verifying the dev server and publishing something else
  // is how three of them got out.
  {
    let synthetic = performance.now()
    ;(window as unknown as Record<string, unknown>).fallLine = {
      skier, hf, chase, world, terrain, spray, sluff, obstacles, obstacleMesh, race, payload, TUNE, controls,
      step(n = 1) {
        for (let i = 0; i < n; i++) { synthetic += 1000 / 60; frame(synthetic) }
        return { z: skier.z, speed: skier.speed, state: race.state, time: race.time }
      },
      restart,
    }
  }
}

let booted = false
function bootOnce(): void {
  if (booted) return
  booted = true
  boot()
}
requestAnimationFrame(() => requestAnimationFrame(bootOnce))
setTimeout(bootOnce, 250)
